import { Button, ButtonColors } from '@chiroup/components/Button';
import { ConfirmModal } from '@chiroup/components/ConfirmModal';
import { FieldValidators } from '@chiroup/core/constants/DatabaseFeatureCommon';
import { OpenClosedStates } from '@chiroup/core/enums/OpenClosedStates.enum';
import { ClinicLocation } from '@chiroup/core/types/Clinic.type';
import { useForm } from '@chiroup/hooks/useForm';
import { EmailValidator } from '@chiroup/hooks/validation/emailValidator';
import React, { use, useEffect, useMemo, useState } from 'react';
import { ToastContext, ToastTypes } from '../../../../contexts/toast.context';
import { useAdminPermissions } from '../../../../hooks/useAdminPermissions';
import SlideOver from '../../../common/SlideOver';
import ClinicLocationDetailForm from './ClinicLocationDetailForm';

const baseValidation = {
  //Todo: add timezone validation when scheduling is implemented
  // timezone: {
  //   required: {
  //     message: 'Time zone is required.',
  //   },
  // },
  country: {
    required: {
      message: 'Country is required.',
    },
  },
  phone: FieldValidators.phone(),
  fax: {
    function: {
      value: (value: Partial<ClinicLocation>) => {
        const { fax } = value;

        if (fax?.[1] === '1' && fax?.split(' ')?.[0]?.length === 6 && fax?.length < 13) {
          return 'Fax must be 7 digits.';
        } else if (fax?.[1] === '1' && fax?.length < 13) {
          return 'Fax must be 10 digits.';
        }

        return false;
      },
    },
  },
  email: {
    pattern: EmailValidator,
  },
};

/**
 * An EHR clinic's location address is the default service facility on its
 * claims (`serviceFacilityLocation` defaults to `clinicLocation`), and the
 * 837 wants ZIP+4 there, so those clinics keep the stricter rule. A non-EHR
 * clinic never files a claim — its address only prints on reports — so it
 * takes a plain 5-digit zip, matching the General tab.
 */
const zipValidation = (isEHR?: boolean) => ({
  function: {
    value: (value: Partial<ClinicLocation>) => {
      const { zip, country } = value;

      //Zip is not a required field here right now so...
      // if (!zip)
      //   return country === 'USA'
      //     ? 'Zip code is required.'
      //     : 'Postal code is required.';
      if (country === 'USA' && !!zip) {
        if (isEHR) {
          const zipCodePattern = /^\d{5}-\d{4}$/;

          if (!zipCodePattern.test(zip)) {
            return 'Zip code must be in the format 12345-6789.';
          }
        } else if (zip.length !== 5 && zip.length !== 10) {
          return 'Zip code must be 5 or 9 digits.';
        }
      }

      return false;
    },
  },
});

type Props = {
  location: Partial<ClinicLocation> | null;
  isEHR?: boolean;
  close: () => void;
  onSubmit: (val: Partial<ClinicLocation>) => Promise<ClinicLocation>;
  onSuccess: (val: Partial<ClinicLocation>) => void;
  removeLocation: () => Promise<void>;
  removingLocation: boolean;
};

const ClinicLocationDetail: React.FC<Props> = ({
  location,
  isEHR,
  close,
  onSubmit,
  onSuccess,
  removeLocation,
  removingLocation,
}) => {
  const { createToast } = use(ToastContext);
  const { hasPermission } = useAdminPermissions();
  const canEdit = hasPermission('clinic.edit_location');
  const [deleteOpen, setDeleteOpen] = useState(OpenClosedStates.Closed);
  const validation = useMemo(() => ({ ...baseValidation, zip: zipValidation(isEHR) }), [isEHR]);
  const { value, onChange, errors, registerSubmit, isDirty, isSubmitting, setValue } =
    useForm<ClinicLocation>(
      location || {
        country: 'USA',
      },
      validation
    );

  useEffect(() => {
    if (location !== null) {
      setValue(location);
    }
  }, [location, setValue]);

  const onFail = (err: any) => {
    createToast({
      title: 'Error',
      description: <span>{err?.response?.data?.message || 'Failed to save location.'}</span>,
      type: ToastTypes.Fail,
      duration: 5000,
    });
  };

  return (
    <SlideOver
      title={location?.ID ? 'Edit Location' : 'Add Location'}
      slideOverState={location ? OpenClosedStates.Open : OpenClosedStates.Closed}
      updateSlideOverState={close}
      buttons={
        <>
          <Button text="Close" onClick={close} color={ButtonColors.plain} />
          {!!value.ID && canEdit && (
            <Button
              text="Delete"
              onClick={() => setDeleteOpen(OpenClosedStates.Open)}
              color={ButtonColors.plain}
            />
          )}
          {canEdit && (
            <Button
              text="Save"
              disabled={!isDirty}
              loading={isSubmitting}
              onClick={registerSubmit(onSubmit, {
                onSuccess,
                onFail,
              })}
            />
          )}
        </>
      }
    >
      <div className="sm:grid sm:grid-cols-4 sm:gap-4 sm:py-5">
        <ClinicLocationDetailForm
          value={value}
          onChange={onChange}
          fieldErrors={errors.fieldErrors}
          disabled={!canEdit}
        />
      </div>
      <ConfirmModal
        isOpen={deleteOpen === OpenClosedStates.Open}
        confirm={async () => {
          try {
            await removeLocation();
            setDeleteOpen(OpenClosedStates.Closed);
            close();
          } catch (err) {
            setDeleteOpen(OpenClosedStates.Closed);
          }
        }}
        close={() => setDeleteOpen(OpenClosedStates.Closed)}
        loading={removingLocation}
        confirmText="Delete"
        description="Are you sure you want to delete this location?"
      />
    </SlideOver>
  );
};

export default ClinicLocationDetail;
